/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react'
import PropTypes from 'prop-types'
import {Box, List, ListItem, Text, useMultiStyleConfig} from '@chakra-ui/react'
import CustomLink from '../custom-link'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'

const LinksList = ({links, columnTitle, variant, children, linkStyle, ...otherProps}) => {
    const styles = useMultiStyleConfig('LinksList', {variant})
    const isHorizontal = variant === 'horizontal'
    const listItemProps = isHorizontal ? styles.listItem : {}
    const listItemSx = isHorizontal ? styles.listItemSx : {}
    const listFlexProps = isHorizontal ? styles.stackWrap : {}
    const {buildUrl} = useMultiSite()

    return (
        <Box {...styles.containerLinks} {...otherProps}>
            {columnTitle && <Text {...styles.heading}>{columnTitle}</Text>}

            <List {...styles.list} {...listFlexProps} flexDir={isHorizontal ? 'row' : 'column'}>
                {links?.map(({link, is_external_url}, index) => {
                    const isExternalUrl = !!is_external_url
                    return (
                        <ListItem key={index} {...listItemProps} sx={listItemSx}>
                            <CustomLink
                                isExternal={isExternalUrl}
                                href={isExternalUrl ? link?.href : buildUrl(link?.href)}
                                {...(linkStyle ? linkStyle : {})}
                            >
                                {link?.title}
                            </CustomLink>
                        </ListItem>
                    )
                })}
                {isHorizontal && children && (
                    <ListItem {...listItemProps} sx={listItemSx}>
                        {children}
                    </ListItem>
                )}
            </List>
        </Box>
    )
}

LinksList.propTypes = {
    variant: PropTypes.oneOf(['vertical', 'horizontal']),
    links: PropTypes.arrayOf(
        PropTypes.shape({
            link: PropTypes.shape({title: PropTypes.string, href: PropTypes.string}),
            is_external_url: PropTypes.bool,
        }),
    ),
    columnTitle: PropTypes.string,
    children: PropTypes.node,
    linkStyle: PropTypes.object,
}

export default LinksList
